import React, { useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx'
import { FHRequestType } from 'stores/fhrequest_store';
import { UserProps } from 'stores/user_store';

import useStores from 'hooks/useStores';
import { CursorType, PagedResult } from "utils/types"
import useUserIdentity from 'hooks/useUserIdentity';


type FHRequestContextType = {

    // Fetch and get one
    fetchRequest: (uuid: string) => Promise<undefined | null>;
    getRequest: (uuid: string) => FHRequestType | null;

    fetchListClaimed: (workKey: string, cursor?: CursorType) => Promise<FHRequestType[]>;
    fetchListClosed: (workKey: string, cursor?: CursorType) => Promise<FHRequestType[]>;
    fetchListClaimedByManager: (manager_identity: string, assistant_identity: string, cursor?: CursorType) => Promise<FHRequestType[]>;
    fetchListByStatus: (status: string, workKey: string, cursor?: CursorType) => Promise<FHRequestType[]>;
    fetchListAvailable: (workKey: string, cursor?: CursorType) => Promise<FHRequestType[]>;
    fetchListSupportAttention: (workKey: string, cursor?: CursorType) => Promise<FHRequestType[]>;
    getListClaimed: (identity: string, user: string) => FHRequestType[];
    getListClosed: (identity: string, user: string) => FHRequestType[];
    getListByStatus: (identity: string, user: string, status: string) => FHRequestType[];

    fetchUserInfoRequest: (request: string) => Promise<UserProps | null>;

    getListAvailable: () => FHRequestType[];
    fetchListByUser: (cursor?: CursorType) => Promise<string>; // returns cursor
    fetchListByProject: (project: string, cursor?: CursorType) => Promise<FHRequestType[]>;
    getListByUser: (user: string) => FHRequestType[];
    getListByIdentity: (identity: string) => FHRequestType[];
    getListByProject: (project: string) => FHRequestType[];

    createRequest: (identity: string, title: string, content: string, project: string|undefined) => Promise<FHRequestType | null>;

    doClaim: (identity: string, request: string) => Promise<boolean>;
    updateStatus: (workKey: string, request: string, status: number, delayTime: string) => Promise<FHRequestType | null>;
    supportAssign: (workKey: string, assign_user: string, request: string) => Promise<boolean>;
    supportInvite: (workKey: string, invite_user: string, request: string) => Promise<boolean>;

    supportUpdatePrice: (workKey: string, request: string, credits: number, price: string) => Promise<FHRequestType | null>;
    supportArchiveRequest: (workKey: string, request: string) => Promise<FHRequestType | null>;

    supportFetchRequests: (workKey: string, lookup_identity: string, cursor?: CursorType) => Promise<PagedResult<FHRequestType> | undefined>;

    supportCloseQuietly: (workKey: string, request: string) => Promise<FHRequestType | null>;

    hasPermission: (request: FHRequestType, user: string, identity: string) => boolean;

    fetchListAllOpen: (workKey: string, cursor?: CursorType) => Promise<string|null>;
    fetchListMentor: (workKey: string, cursor?: CursorType) => Promise<FHRequestType[]>;

    items: FHRequestType[];


}

export const FHRequestContext = React.createContext<FHRequestContextType>({

    fetchRequest: () => { throw 'Not Implemented' },
    getRequest: () => { throw 'Not Implemented' },
    getListAvailable: () => { throw 'Not Implemeneted' },
    getListClaimed: () => { throw 'Not Implemented' },
    getListClosed: () => { throw 'Not Implemented' },
    getListByStatus: () => { throw 'Not Implemented' },
    fetchUserInfoRequest: () => { throw 'Not Implemented' },
    fetchListClaimed: () => { throw 'Not Implemented' },
    fetchListClosed: () => { throw 'Not Implemented' },
    fetchListByStatus: () => { throw 'Not Implemented' },
    fetchListClaimedByManager: () => { throw 'Not Implemented' },
    fetchListSupportAttention: () => { throw 'Not Implemented' },
    fetchListAvailable: () => { throw 'Not Implemented' },
    fetchListByUser: () => { throw 'Not Implemented' },
    fetchListByProject: () => { throw 'Not Implemented' },
    getListByIdentity: () => { throw 'Not Implemented' },
    getListByUser: () => { throw 'Not Implemented' },
    getListByProject: () => { throw 'Not Implemented' },
    createRequest: () => { throw 'Not Implemented' },
    doClaim: () => { throw 'Not Implemented' },
    supportAssign: () => { throw 'Not Implemented' },
    supportInvite: () => { throw 'Not Implemented' },
    updateStatus: () => { throw 'Not Implemented' },
    supportUpdatePrice: () => { throw 'Not Implemented' },
    supportArchiveRequest: () => { throw 'Not Implemented' },
    supportFetchRequests: () => { throw 'Not Implemented' },
    supportCloseQuietly: () => { throw 'Not Implemented' },
    hasPermission: () => { throw 'Not Implemented' },
    fetchListAllOpen: () => { throw 'Not Implemented' },
    fetchListMentor: () => { throw 'Not Implemented' },
    items: [],
});


export const FHRequestProvider = observer(({ children }: { children: React.ReactNode }) => {
    const [items, setItems] = useState([] as FHRequestType[]);
    const { fhRequestStore } = useStores();
    const { currentIdentity } = useUserIdentity();
    const [previousIdentity, setPreviousIdentity] = useState(currentIdentity?.uuid);

    useEffect(() => {
        if (previousIdentity && (currentIdentity.uuid != previousIdentity)) {
            setPreviousIdentity(currentIdentity.uuid);
            runInAction(() => {
                fhRequestStore.items = [];
            });
            console.log(`clearing requests: ${currentIdentity.uuid} != ${previousIdentity}`);
        }
    }, [currentIdentity.uuid, setPreviousIdentity, previousIdentity]);


    const fetchRequest = async (uuid: string) => {
        return fhRequestStore.fetchRequest(uuid);
    };

    const getRequest = (key: string) => {
        return fhRequestStore.items.find((item: FHRequestType) => {
            if(item.key === key)
                return item;
            else if(item.uuid === key)
                return item;
            else if(item.ogkey === key)
                return item;
            else
                return null;
        }) || null;
    };

    const fetchUserInfoRequest = async (request: string) => {
        return fhRequestStore.fetchUserInfoRequest(request);
    };

    const fetchListByUser = async (cursor?: CursorType) => {
        return fhRequestStore.fetchListByUser(cursor);
    };

    const fetchListByProject = async (project: string, cursor?: CursorType) => {
        return fhRequestStore.fetchListByProject(project, cursor);
    };

    const fetchListSupportAttention = async (workKey: string, cursor?: CursorType) => {
        return fhRequestStore.fetchListSupportAttention(workKey, cursor);
    };


    const fetchListAvailable = async (workKey: string, cursor?: CursorType) => {
        return fhRequestStore.fetchListAvailable(workKey, cursor)
            .then(() => {
                return getListAvailable();
            });
    };

    const fetchListByStatus = async (workKey: string, status: string, cursor?: CursorType) => {
        /** fetch the list by status similar to collabs
        so "active" == "claimed"
        and "archived" == "closed"
        */
        if(status === "active")
            return fetchListClaimed(workKey, cursor);
        else if (status === 'archived')
            return fetchListClosed(workKey, cursor);
        else {
            console.error(`Unknown status: ${status}`);
            return [];
        }
    };

    const fetchListClaimed = async (workKey: string, cursor?: CursorType) => {
        console.log('fetchListClaimed', workKey, cursor);
        return fhRequestStore.fetchListClaimed(workKey, cursor);
    };

    const fetchListClosed = async (workKey: string, cursor?: CursorType) => {
        return fhRequestStore.fetchListClosed(workKey, cursor);
    };

    const supportFetchRequests = async (workKey: string, lookup_identity: string, cursor?: CursorType) => {
        return fhRequestStore.supportFetchRequests(workKey, lookup_identity, cursor || '');
    };

    const fetchListClaimedByManager = async (manager_identity: string,
        assistant_identity: string,
        cursor?: CursorType) => {
            return fhRequestStore.fetchListClaimedByManager(manager_identity, assistant_identity, cursor);
    };

    const getListAvailable = () => {
        return fhRequestStore.items.filter((item: FHRequestType) =>
            ((item.claimed === false)));
    };

    const getListByUser = (user: string) => {
        return fhRequestStore.items.filter((item: FHRequestType) => item.user === user);
    };

    const getListByIdentity = (identity: string) => {
        return fhRequestStore.items.filter((item: FHRequestType) => item.identity === identity);
    };

    const getListByProject = (project: string) => {
        return fhRequestStore.items.filter((item: FHRequestType) => item.project === project);
    };

    const getListClaimed = (identity: string, user: string) => {
        return fhRequestStore.items.filter((item: FHRequestType) =>
            {
                if(item.status < 20 &&
                    ((item.claimed_by === user) ||
                        (item.claimed_identity === identity))) {
                    return true;
                }
                return false;
            }
        );
    };

    const getListClosed = (identity: string, user: string) => {
        return fhRequestStore.items.filter((item: FHRequestType) =>
            {
                if(item.status >= 20 &&
                    ((item.claimed_by === user) ||
                        (item.claimed_identity === identity))) {
                    return true;
                }
                return false;
            }
        );
    };

    const getListByStatus = (identity: string, user: string, status: string) => {
        if (status === 'active')
            return getListClaimed(identity, user);
        else if (status === 'archived')
            return getListClosed(identity, user);
        else
            return [];
    };

    const createRequest = async (identity: string, title: string, content: string, project: string|undefined) => {
        return fhRequestStore.createRequest(identity, title, content, project || '')
            .then((newRequest: FHRequestType | null) => {

                if (newRequest) {
                    setItems([...items, newRequest]);
                }
                return newRequest;
            });
    };

    const doClaim = async (identity: string, request: string) => {
        return fhRequestStore.doClaim(identity, request)
            .then((success: boolean) => {
                if (success) {
                    setItems(fhRequestStore.items);
                }
                return success;
            });
    };

    const supportAssign = async (workKey: string, assign_user: string, request: string) => {
        return fhRequestStore.supportAssign(workKey, assign_user, request)
            .then((success: boolean) => {
                if (success) {
                    setItems(fhRequestStore.items);
                }
                return success;
            });
    };

    const supportInvite = async (workKey: string, invite_user: string, request: string) => {
        return fhRequestStore.supportInvite(workKey, invite_user, request)
            .then((success: boolean) => {
                if (success) {
                    setItems(fhRequestStore.items);
                }
                return success;
            });
    };

    const supportArchiveRequest = async (workKey: string, request: string) => {
        return fhRequestStore.supportArchiveRequest(workKey, request)
            .then((request: FHRequestType) => {
                setItems(fhRequestStore.items);
                return request;
            });
    }

    const updateStatus = async (workKey: string, request: string, status: number, delayTime: string) => {
        return fhRequestStore.updateStatus(workKey, request, status, delayTime)
            .then((req: FHRequestType | null) => {
                return req;
            });
    };

    const hasPermission = (request: FHRequestType, user: string, identity: string) => {
        if(request.claimed_identity == identity)
            return true;
        if(request.claimed_by == user)
            return true;

        return false;
    };

    const supportUpdatePrice = async (workKey: string, request: string, credits: number, price: string) => {
        return fhRequestStore.supportUpdatePrice(workKey, request, credits, price)
            .then((req: FHRequestType | null) => {
                return req;
            });
    };

    const supportCloseQuietly = async (workKey: string, request: string) => {
        return fhRequestStore.supportCloseQuietly(workKey, request)
            .then((req: FHRequestType | null) => {
                return req;
            });
    };

    const fetchListAllOpen = async (workKey: string, cursor?: CursorType) => {
        return fhRequestStore.fetchListAllOpen(workKey, cursor);
    };

    const fetchListMentor = async (workKey: string, cursor?: CursorType) => {
        return fhRequestStore.fetchListMentor(workKey, cursor);
    };

    const data = {
        fetchRequest,
        getRequest,
        fetchListClaimed,
        fetchListClosed,
        fetchListClaimedByManager,
        fetchListByStatus,
        getListByStatus,
        getListClosed,
        getListClaimed,
        getListAvailable,
        fetchListAvailable,
        fetchListByUser,
        fetchListByProject,
        fetchUserInfoRequest,
        fetchListSupportAttention,
        getListByIdentity,
        getListByUser,
        getListByProject,
        createRequest,
        doClaim,
        updateStatus,
        hasPermission,
        items: fhRequestStore.items,
        supportUpdatePrice,
        supportAssign,
        supportInvite,
        supportArchiveRequest,
        supportFetchRequests,
        supportCloseQuietly,
        fetchListAllOpen,
        fetchListMentor,
    };

    return (
        <FHRequestContext.Provider value={data}>{ children }</FHRequestContext.Provider>
    );

});
